import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Activity, Calendar, BarChart3, Download } from "lucide-react";
import { format } from "date-fns";
import { mockDataStore } from "../components/utils/mockData";

import TrendChart from "../components/analytics/TrendChart";
import MetricCard from "../components/analytics/MetricCard";
import ComparisonChart from "../components/analytics/ComparisonChart";
import HealthScore from "../components/analytics/HealthScore";

export default function Analytics() {
  const [selectedPatient, setSelectedPatient] = useState("PAT001");
  const [selectedMetric, setSelectedMetric] = useState("glucose");
  const [timeRange, setTimeRange] = useState("6months");
  const [isLoading] = useState(false);

  // Get data directly from mock store
  const bloodTests = mockDataStore.data.bloodTests
    .filter((test) => test.patient_id === selectedPatient)
    .sort((a, b) => new Date(a.test_date) - new Date(b.test_date));

  const wearableData = mockDataStore.data.wearableData.filter(
    (data) => data.patient_id === selectedPatient
  );

  // Calculate metrics
  const latestTest = bloodTests[bloodTests.length - 1];
  const previousTest = bloodTests[bloodTests.length - 2];

  // Prepare chart data
  const chartData = bloodTests.map((test) => ({
    date: format(new Date(test.test_date), "MMM dd"),
    value: test.results?.[selectedMetric] || 0,
    test_type: test.test_type,
  }));

  const metrics = [
    { key: "glucose", label: "Glucose", unit: "mg/dL", color: "#3B82F6" },
    {
      key: "cholesterol_total",
      label: "Total Cholesterol",
      unit: "mg/dL",
      color: "#EF4444",
    },
    { key: "hemoglobin", label: "Hemoglobin", unit: "g/dL", color: "#10B981" },
    { key: "white_blood_cells", label: "WBC", unit: "/μL", color: "#F59E0B" },
    { key: "cholesterol_hdl", label: "HDL Cholesterol", unit: "mg/dL", color: "#8B5CF6" },
    { key: "cholesterol_ldl", label: "LDL Cholesterol", unit: "mg/dL", color: "#F97316" },
    { key: "triglycerides", label: "Triglycerides", unit: "mg/dL", color: "#06B6D4" },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Health Analytics
          </h1>
          <p className="text-slate-600">
            Track biomarkers and health trends over time
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export Data
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Select value={selectedPatient} onValueChange={setSelectedPatient}>
          <SelectTrigger className="bg-white/60 backdrop-blur-sm border-slate-200/60">
            <Activity className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Select patient" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PAT001">PAT001 - John Doe</SelectItem>
            <SelectItem value="PAT002">PAT002 - Jane Smith</SelectItem>
            <SelectItem value="PAT003">PAT003 - Michael Brown</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedMetric} onValueChange={setSelectedMetric}>
          <SelectTrigger className="bg-white/60 backdrop-blur-sm border-slate-200/60">
            <BarChart3 className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Select metric" />
          </SelectTrigger>
          <SelectContent>
            {metrics.map((metric) => (
              <SelectItem key={metric.key} value={metric.key}>
                {metric.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="bg-white/60 backdrop-blur-sm border-slate-200/60">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="3months">Last 3 Months</SelectItem>
            <SelectItem value="6months">Last 6 Months</SelectItem>
            <SelectItem value="1year">Last Year</SelectItem>
          </SelectContent>
        </Select>

        <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <Activity className="w-5 h-5 text-blue-600" />
            <div>
              <div className="text-sm font-medium text-slate-900">
                {bloodTests.length} tests tracked
              </div>
              <div className="text-xs text-slate-600">
                {wearableData.length} wearable data points
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.slice(0, 4).map((metric) => {
          const value = latestTest?.results?.[metric.key] || 0;
          const prevValue = previousTest?.results?.[metric.key] || 0;
          const metricChange = value - prevValue;
          const isImproving =
            metric.key === "cholesterol_total" || metric.key === "cholesterol_ldl" || metric.key === "triglycerides"
              ? metricChange < 0
              : metricChange > 0;
          const trendDirection = metricChange >= 0 ? "up" : "down";

          return (
            <MetricCard
              key={metric.key}
              title={metric.label}
              value={value}
              unit={metric.unit}
              change={metricChange}
              trend={trendDirection}
              isImproving={isImproving}
              color={metric.color}
              isSelected={selectedMetric === metric.key}
              onClick={() => setSelectedMetric(metric.key)}
            />
          );
        })}
      </div>

      {/* Additional Metrics */}
      {metrics.length > 4 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {metrics.slice(4).map((metric) => {
            const value = latestTest?.results?.[metric.key] || 0;
            const prevValue = previousTest?.results?.[metric.key] || 0;
            const metricChange = value - prevValue;
            const isImproving =
              metric.key === "cholesterol_total" || metric.key === "cholesterol_ldl" || metric.key === "triglycerides"
                ? metricChange < 0
                : metricChange > 0;
            const trendDirection = metricChange >= 0 ? "up" : "down";

            return (
              <MetricCard
                key={metric.key}
                title={metric.label}
                value={value}
                unit={metric.unit}
                change={metricChange}
                trend={trendDirection}
                isImproving={isImproving}
                color={metric.color}
                isSelected={selectedMetric === metric.key}
                onClick={() => setSelectedMetric(metric.key)}
              />
            );
          })}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Trend Chart */}
        <div className="lg:col-span-2">
          <TrendChart
            data={chartData}
            metric={selectedMetric}
            metricLabel={metrics.find((m) => m.key === selectedMetric)?.label}
            color={metrics.find((m) => m.key === selectedMetric)?.color}
            isLoading={isLoading}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <HealthScore bloodTests={bloodTests} wearableData={wearableData} />

          <ComparisonChart bloodTests={bloodTests} timeRange={timeRange} />

          {/* Latest Test Results */}
          <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
            <CardHeader>
              <CardTitle className="text-slate-900">Latest Results</CardTitle>
            </CardHeader>
            <CardContent>
              {latestTest ? (
                <div className="space-y-3">
                  <div className="text-sm text-slate-600">
                    {format(new Date(latestTest.test_date), "MMM d, yyyy")}
                  </div>
                  {Object.entries(latestTest.results).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex justify-between items-center"
                    >
                      <span className="text-sm text-slate-700 capitalize">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className="font-medium text-slate-900">
                        {typeof value === "number" ? value.toFixed(1) : value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  No test results available
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
